import React from "react";
import { styled } from "@mui/material/styles";

const Container = styled("div")({
  maxWidth: "1400px",
  margin: "0 auto",
  backgroundColor: "white",
  padding: "30px",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
});

const Title = styled("h1")({
  textAlign: "center",
  fontSize: "28px",
  fontWeight: 600,
  color: "#1a1a1a",
  marginBottom: "15px",
});

const CurrencyNote = styled("p")({
  textAlign: "center",
  fontSize: "14px",
  color: "#666",
  marginTop: "10px",
  fontStyle: "italic",
});

const StyledTable = styled("table")({
  width: "100%",
  borderCollapse: "collapse",
  marginTop: "20px",
  fontSize: "14px",
  "& th, & td": {
    border: "1px solid #ddd",
    padding: "12px 15px",
    textAlign: "center",
  },
  "& th": {
    backgroundColor: "#f8f9fa",
    fontWeight: 600,
    color: "#1a1a1a",
  },
  "& .header-row": {
    backgroundColor: "#e9ecef",
  },
  "& .country-column": {
    textAlign: "left",
    fontWeight: 500,
  },
  "& .plan-header": {
    backgroundColor: "#f8f9fa",
    fontSize: "16px",
    fontWeight: 600,
    padding: "15px",
  },
  "& .amount-header": {
    backgroundColor: "#f8f9fa",
    fontWeight: 600,
    verticalAlign: "middle",
  },
  "& tbody tr:nth-of-type(even)": {
    backgroundColor: "#fafafa",
  },
  "& tbody tr:hover": {
    backgroundColor: "#f0f0f0",
  },
  "@media (max-width: 1200px)": {
    fontSize: "12px",
    "& th, & td": {
      padding: "8px 10px",
    },
  },
  "@media (max-width: 768px)": {
    fontSize: "11px",
    "& th, & td": {
      padding: "6px 8px",
    },
  },
});

// Bump on every Meta pricing update together with pricingData below; users whose stored
// acknowledgement differs from this see the pricing popup again (App.tsx)
export const WHATSAPP_PRICING_VERSION = "2026-10-01";

// Rates since Oct 1, 2026 (geta-core src/shared/scripts/update_whatsapp_pricing_oct_2026.py).
// Each cell = round(base, 3) + round(base * markup / 100, 3), the same price + margin the wallet is
// charged, with base from whatsapp_rate_card_2026 and the plan's percent_increment markup.
const pricingData = [
  { country: "Argentina", starter: [5.027, 2.478, 2.097], growth: [4.982, 2.383, 2.097], pro: [4.937, 2.287, 2.097] },
  { country: "Bangladesh", starter: [5.956, 0.352, 0.298], growth: [5.903, 0.339, 0.298], pro: [5.849, 0.325, 0.298] },
  { country: "Brazil", starter: [5.085, 0.648, 0.548], growth: [5.039, 0.623, 0.548], pro: [4.993, 0.598, 0.548] },
  { country: "Chile", starter: [7.229, 1.905, 1.612], growth: [7.164, 1.831, 1.612], pro: [7.099, 1.758, 1.612] },
  { country: "Colombia", starter: [1.017, 0.077, 0.065], growth: [1.008, 0.074, 0.065], pro: [0.998, 0.071, 0.065] },
  { country: "Egypt", starter: [5.238, 0.343, 0.290], growth: [5.191, 0.330, 0.290], pro: [5.144, 0.317, 0.290] },
  { country: "France", starter: [6.992, 2.859, 2.419], growth: [6.929, 2.749, 2.419], pro: [6.866, 2.639, 2.419] },
  { country: "Germany", starter: [11.108, 5.242, 4.435], growth: [11.008, 5.040, 4.435], pro: [10.908, 4.838, 4.435] },
  { country: "Hong Kong", starter: [5.956, 2.478, 2.097], growth: [5.903, 2.382, 2.097], pro: [5.849, 2.287, 2.097] },
  { country: "Hungary", starter: [6.993, 3.325, 2.814], growth: [6.930, 3.198, 2.814], pro: [6.867, 3.070, 2.814] },
  { country: "India", starter: [0.958, 0.150, 0.127], growth: [0.949, 0.144, 0.127], pro: [0.941, 0.138, 0.127], highlight: true },
  { country: "Indonesia", starter: [3.342, 2.381, 2.015], growth: [3.312, 2.290, 2.015], pro: [3.282, 2.198, 2.015] },
  { country: "Iraq", starter: [2.774, 0.753, 0.637], growth: [2.749, 0.724, 0.637], pro: [2.724, 0.695, 0.637] },
  { country: "Israel", starter: [2.872, 0.506, 0.428], growth: [2.846, 0.486, 0.428], pro: [2.820, 0.467, 0.428] },
  { country: "Italy", starter: [6.461, 2.856, 2.417], growth: [6.403, 2.746, 2.417], pro: [6.345, 2.636, 2.417] },
  { country: "Kazakhstan", starter: [4.912, 1.715, 1.451], growth: [4.867, 1.649, 1.451], pro: [4.823, 1.583, 1.451] },
  { country: "Kuwait", starter: [6.445, 4.190, 3.545], growth: [6.387, 4.029, 3.545], pro: [6.329, 3.868, 3.545] },
  { country: "Malaysia", starter: [6.997, 1.334, 1.129], growth: [6.934, 1.283, 1.129], pro: [6.871, 1.231, 1.129] },
  { country: "Mexico", starter: [3.226, 0.810, 0.685], growth: [3.197, 0.779, 0.685], pro: [3.168, 0.748, 0.685] },
  { country: "Morocco", starter: [3.369, 2.190, 1.853], growth: [3.339, 2.106, 1.853], pro: [3.308, 2.022, 1.853] },
  { country: "Nepal", starter: [5.956, 0.324, 0.274], growth: [5.903, 0.311, 0.274], pro: [5.849, 0.299, 0.274] },
  { country: "Netherlands", starter: [12.996, 4.766, 4.033], growth: [12.879, 4.582, 4.033], pro: [12.762, 4.399, 4.033] },
  { country: "Nigeria", starter: [4.201, 0.638, 0.540], growth: [4.164, 0.614, 0.540], pro: [4.126, 0.589, 0.540] },
  { country: "Oman", starter: [2.774, 2.352, 1.990], growth: [2.749, 2.261, 1.990], pro: [2.724, 2.171, 1.990] },
  { country: "Pakistan", starter: [3.850, 1.430, 1.210], growth: [3.815, 1.375, 1.210], pro: [3.780, 1.320, 1.210] },
  { country: "Peru", starter: [5.721, 2.859, 2.419], growth: [5.669, 2.749, 2.419], pro: [5.618, 2.639, 2.419] },
  { country: "Poland", starter: [2.969, 1.160, 0.981], growth: [2.943, 1.115, 0.981], pro: [2.916, 1.070, 0.981] },
  { country: "Qatar", starter: [2.774, 1.143, 0.967], growth: [2.749, 1.099, 0.967], pro: [2.724, 1.055, 0.967] },
  { country: "Romania", starter: [6.993, 2.756, 2.332], growth: [6.930, 2.650, 2.332], pro: [6.867, 2.544, 2.332] },
  { country: "Russia", starter: [6.523, 3.810, 3.224], growth: [6.465, 3.664, 3.224], pro: [6.406, 3.517, 3.224] },
  { country: "Saudi Arabia", starter: [4.674, 1.018, 0.861], growth: [4.632, 0.979, 0.861], pro: [4.590, 0.940, 0.861] },
  { country: "Singapore", starter: [5.956, 1.525, 1.290], growth: [5.903, 1.466, 1.290], pro: [5.849, 1.408, 1.290] },
  { country: "South Africa", starter: [3.088, 0.906, 0.767], growth: [3.060, 0.871, 0.767], pro: [3.032, 0.836, 0.767] },
  { country: "Spain", starter: [5.750, 1.904, 1.611], growth: [5.698, 1.831, 1.611], pro: [5.646, 1.758, 1.611] },
  { country: "Sri Lanka", starter: [5.956, 0.191, 0.162], growth: [5.903, 0.184, 0.162], pro: [5.849, 0.176, 0.162] },
  { country: "Turkey", starter: [0.887, 0.086, 0.073], growth: [0.879, 0.083, 0.073], pro: [0.871, 0.079, 0.073] },
  { country: "Ukraine", starter: [6.993, 2.831, 2.396], growth: [6.930, 2.723, 2.396], pro: [6.867, 2.614, 2.396] },
  { country: "United Arab Emirates", starter: [4.674, 1.496, 1.266], growth: [4.632, 1.439, 1.266], pro: [4.590, 1.381, 1.266] },
  { country: "United Kingdom", starter: [5.161, 2.096, 1.773], growth: [5.115, 2.015, 1.773], pro: [5.068, 1.934, 1.773] },
  { country: "North America", starter: [2.032, 0.324, 0.274], growth: [2.014, 0.311, 0.274], pro: [1.996, 0.299, 0.274] },
  { country: "Rest of Africa", starter: [1.831, 0.381, 0.322], growth: [1.815, 0.366, 0.322], pro: [1.798, 0.352, 0.322] },
  { country: "Rest of Asia Pacific", starter: [6.850, 1.076, 0.911], growth: [6.788, 1.035, 0.911], pro: [6.726, 0.994, 0.911] },
  { country: "Rest of Central & Eastern Europe", starter: [6.993, 2.015, 1.705], growth: [6.930, 1.937, 1.705], pro: [6.867, 1.860, 1.705] },
  { country: "Rest of Latin America", starter: [6.017, 1.076, 0.911], growth: [5.963, 1.035, 0.911], pro: [5.909, 0.994, 0.911] },
  { country: "Rest of Middle East", starter: [3.190, 0.866, 0.733], growth: [3.161, 0.833, 0.733], pro: [3.133, 0.799, 0.733] },
  { country: "Rest of Western Europe", starter: [4.815, 1.627, 1.377], growth: [4.772, 1.565, 1.377], pro: [4.728, 1.502, 1.377] },
  { country: "Other", starter: [4.912, 0.733, 0.620], growth: [4.867, 0.705, 0.620], pro: [4.823, 0.677, 0.620] },
];

const WhatsappPricingTable: React.FC = () => {
  return (
    <div style={{ padding: "20px", backgroundColor: "#f5f5f5", minHeight: "calc(100vh - 70px)" }}>
      <Container>
        <Title>Country Wise WhatsApp Message Cost</Title>
        <CurrencyNote>Amount in INR per message, by recipient country</CurrencyNote>

        <StyledTable>
          <thead>
            <tr className="header-row">
              <th rowSpan={2} className="amount-header">
                To Recipient
                <br />
                Country
              </th>
              <th colSpan={3} className="plan-header">
                Starter Plan
              </th>
              <th colSpan={3} className="plan-header">
                Growth Plan
              </th>
              <th colSpan={3} className="plan-header">
                Pro Plan
              </th>
            </tr>
            <tr className="header-row">
              <th>Marketing</th>
              <th>Utility</th>
              <th>Authentication</th>
              <th>Marketing</th>
              <th>Utility</th>
              <th>Authentication</th>
              <th>Marketing</th>
              <th>Utility</th>
              <th>Authentication</th>
            </tr>
          </thead>
          <tbody>
            {pricingData.map((row, index) => (
              <tr key={index} style={row.highlight ? { fontWeight: "bold" } : {}}>
                <td className="country-column" style={row.highlight ? { fontWeight: "bold" } : {}}>
                  {row.country}
                </td>
                <td>{row.starter[0].toFixed(3)}</td>
                <td>{row.starter[1].toFixed(3)}</td>
                <td>{row.starter[2].toFixed(3)}</td>
                <td>{row.growth[0].toFixed(3)}</td>
                <td>{row.growth[1].toFixed(3)}</td>
                <td>{row.growth[2].toFixed(3)}</td>
                <td>{row.pro[0].toFixed(3)}</td>
                <td>{row.pro[1].toFixed(3)}</td>
                <td>{row.pro[2].toFixed(3)}</td>
              </tr>
            ))}
          </tbody>
        </StyledTable>
        <CurrencyNote>Service messages are charged at the Utility rate for your plan. The first 1,000 service messages per WhatsApp business number each month are free.</CurrencyNote>
      </Container>
    </div>
  );
};

export default WhatsappPricingTable;
